import React, { MouseEvent, useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classnames from 'classnames';

import LottoContext from '../Context/Lotto.context';
import { RootState } from '../../../configs/store';
import './DrawCalendar.scss';
import ToolTip from '../../ToolTip/ToolTip';

interface SingleEvent {
  code: number,
  selected: boolean,
  scheduled: number
}

export default function DrawCalendar() {
  const { t } = useTranslation('common');
  const {
    singleNumberEvent: { events }
  } = useSelector((state: RootState) => state.lotto);
  const { selectedTimezone } = useSelector((state: RootState) => state.odds);
  const { bets } = useSelector((state: RootState) => state.betslip);
  const { selectedEvents, setSelectedEvents, selectedSpecialEvents, setSelectedSpecialEvents, tab } = useContext(LottoContext);
  const [specialError, setSpecialError] = useState(false);

  useEffect(() => {
    if ((_.isEmpty(selectedEvents) || _.isEmpty(selectedSpecialEvents)) && !_.isEmpty(events)){
      setSelectedEvents(events);
      setSelectedSpecialEvents(events);
    }
  }, [events, selectedEvents, selectedSpecialEvents, setSelectedEvents, setSelectedSpecialEvents]);

  useEffect(() => {
    // If we already have some bets of the betslip from this event we should start with the calendar selections
    if (events && tab >= 800){
      const previouslySelected = [];
      for (const event of selectedSpecialEvents){
        if (_.findIndex(bets, { eventCode: event.code, typeCode:800 }) >= 0){
          const newEvent = JSON.parse(JSON.stringify(event));
          newEvent.selected = true;
          previouslySelected.push(newEvent);
        } else {
          previouslySelected.push(event);
        }
      }
      setSelectedSpecialEvents(previouslySelected);
    }
  }, [tab]);

  const handleClick = (singleEvent:SingleEvent) => (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { code, selected } = singleEvent;
    const numberOfSelectedEvents = _.filter(selectedEvents, { selected: true });
    // Rules for markets <= 800
    if (tab <= 800){

      if (selected && numberOfSelectedEvents.length > 1) {
        const selectedIndex = _.findIndex(selectedEvents, { code });
        const deleteSelected = _.map(selectedEvents, (ev, index) => {
          if (index >= selectedIndex && selectedEvents[selectedIndex-1]?.selected) {
            return { ...ev, selected: false };
          } else if (index === selectedIndex && selectedEvents[selectedIndex-1]?.selected === false){
            return { ...ev, selected: false };
          } else if (index === selectedIndex && selectedEvents[selectedIndex-1]?.selected === undefined){
            return { ...ev, selected: false };
          }
          return { ...ev };
        });
        setSelectedEvents(deleteSelected);
      } else {
        const indexOfSelected = _.findIndex(selectedEvents, {
          code
        });
        const isPossible = selectedEvents[indexOfSelected -1 ]?.selected;
        if (isPossible) {
          const addSelected = _.map(selectedEvents, (ev) => {
            if (ev.code === singleEvent.code) {
              return { ...ev, selected: true };
            }
            return { ...ev };
          });
          setSelectedEvents(addSelected);
        } else {
          const deleteAllOther = _.map(selectedEvents, (ev) => {
            if (ev.code !== singleEvent.code) {
              return { ...ev, selected: false };
            } else {
              return { ...ev, selected: true };
            }
          });
          setSelectedEvents(deleteAllOther);
        }
      }
    } else {
      const selections = _.filter(selectedSpecialEvents, { selected: true });
      const alreadyOnBetslip = bets.some((bet) => _.findIndex(selections, { code: bet.eventCode  }) >=0 && bet.typeCode === 800);

      if (alreadyOnBetslip){
        setSpecialError(true);
      } else {
        const { selected } = singleEvent;

        if (selected && selections.length > 1){
          const removeSelected = _.map(selectedSpecialEvents, (ev) => {
            if (ev.code === singleEvent.code) {
              return { ...ev, selected: false };
            }

            return { ...ev };
          });

          setSelectedSpecialEvents(removeSelected);
        } else {

          const addSelected = _.map(selectedSpecialEvents, (ev) => {
            if (ev.code === singleEvent.code) {
              return { ...ev, selected: true };
            }

            return { ...ev };
          });

          setSelectedSpecialEvents(addSelected);
        }
      }
    }
  };
  const tooltipClick = () => {
    setSpecialError(false);
  };

  return (
    <div className="draw-calendar">
      <div className="draw-calendar__info">
        {
          specialError &&  <ToolTip
            title= { t('numbers.invalidUpdate') }
            message={ t('numbers.removeOutcomes') }
            className="draw-calendar__tooltip--hidden"
            isOpen
            handleClick={ tooltipClick }
          />
        }
        <ToolTip
          number={ 1 }
          title={ t('numbers.pickYourDraw') }
          message={ t('numbers.toolTip1') }
          className="draw-calendar__number"
        />
      </div>
      <div className="draw-calendar__cards">
        {
          selectedEvents && selectedSpecialEvents  &&
          _.map(tab <= 800 ? selectedEvents : selectedSpecialEvents, (singleEvent, index) => {
            const { scheduled, selected } = singleEvent;
            const date = utcToZonedTime(scheduled * 1000, selectedTimezone);

            return (
              <div
                className={
                  classnames({
                    'draw-calendar__card': true,
                    'draw-calendar__card--selected': selected
                  })
                }
                onClick={ handleClick(singleEvent) }
                key={ index }
              >
                <div
                  className={
                    classnames({
                      'draw-calendar__date': true,
                      'draw-calendar__date--selected': selected
                    })
                  }
                >
                  { format(date, 'EEE, dd MMM') }
                </div>
                <div
                  className={
                    classnames({
                      'draw-calendar__time': true,
                      'draw-calendar__time--selected': selected
                    })
                  }
                >
                  { format(date, 'HH:mm') }
                </div>
              </div>
            );
          })
        }
      </div>
    </div>
  );
}
